/**
 * rmvpe-pitch.js — page-side facade of the RMVPE vocal-pitch tracker
 * (window.RmvpePitch). The network runs in rmvpe-worker.js; the pure recipe
 * lives in rmvpe-shared.js (one copy for the worker and the unit tests).
 *
 * What it answers: «which note is the SINGER on, every 10 ms» — straight from a
 * full song, no separation pass. Measured 2026-09-23 against human annotation
 * (numbers and method: rmvpe-shared.js header; memory tool_audio_to_notes).
 *
 * Public API:
 *   support({allowWasm})          → Promise<{ok, reason?, providers}>   cheap, downloads nothing
 *   preload(slug, {signal, onProgress, allowWasm}) → Promise<{provider, chunkMs}>
 *   track(audioBuffer, {signal, onProgress, slug, allowWasm})
 *       → Promise<{ f0:Float32Array, conf:Float32Array, hop:0.01, frames, provider, ms, stats }>
 *   toFrames(result)              → [{timeS, freqHz, confidence}] — the shape
 *                                   __atnClean.segmentNeuralFrames takes
 *   release()                     → terminates the worker (the only way wasm memory returns)
 *   estimateWorkS(durationS, chunkMs) → seconds the network will work on a song of that length,
 *                                   from the chunk time it measured at its start (null if unknown)
 *   tempo()                       → { chunkMs, ageMs } | null — the fastest chunk this device's card
 *                                   has timed in the last 30 days: a page can decide before any download
 *   track(…, { gate(info) })      → asked once the engine is ready and before any work on the song:
 *                                   `false` ends the pass with code 'declined' (not a failure, no
 *                                   telemetry of one) — the caller saw info.chunkMs and chose not to go on
 *
 * Wiring comes from the page (tools/partials/rmvpe-engine): the worker URL and
 * the model URL pair are <meta> tags, the ORT build is window.__ortBuild
 * (OrtBuildSingleOwnerTest). WebKit gets the plain WASM build only — the
 * owner's rule, measured for the JSEP build on WebKit 26.
 */
(function () {
'use strict';

var SR = 16000, HOP_S = 0.01;
var MIN_DEVICE_GB = 4;          // 90 M float32 weights in wasm/GPU memory + activations of one chunk

function meta(name) { var m = document.querySelector('meta[name="' + name + '"]'); return (m && m.content) || ''; }
function abs(u) { try { return new URL(u, window.location.href).href; } catch (_) { return u; } }
function tele(slug, action, payload) {
    try { if (typeof window.__ae === 'function') window.__ae('feature_use', Object.assign({ slug: slug || 'rmvpe', action: action }, payload || {})); } catch (_) {}
}
function typed(code, message) { var e = new Error(message || code); e.code = code; return e; }

function loadScript(path, probe) {
    if (probe()) return Promise.resolve();
    return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = window.__jsSrc ? window.__jsSrc(path) : path;
        s.onload = function () { probe() ? resolve() : reject(typed('dep_missing', path + ' loaded without its global')); };
        s.onerror = function () { reject(typed('dep_missing', path + ' failed to load')); };
        document.head.appendChild(s);
    });
}
function needVerifier() { return loadScript('/js/ort-ep-verify.js', function () { return !!window.OrtEpVerify; }); }
function needDownloader() { return loadScript('/js/model-download.js', function () { return !!self.__mdFetchBytes; }); }

var adapterInfo = null, cacheKey = null;

/** Which ORT build and which providers this device gets — decided before a byte of the model moves. */
function support(opts) {
    opts = opts || {};
    var allowWasm = !!opts.allowWasm;
    if (!window.Worker) return Promise.resolve({ ok: false, reason: 'no_worker' });
    if (!meta('rmvpe-worker-url') || !meta('rmvpe-model-url')) return Promise.resolve({ ok: false, reason: 'not_wired' });
    if (!window.__ortBuild) return Promise.resolve({ ok: false, reason: 'not_wired' });
    var mem = navigator.deviceMemory;
    if (typeof mem === 'number' && mem < MIN_DEVICE_GB) return Promise.resolve({ ok: false, reason: 'low_memory' });
    var native = typeof window.__ortBuild.nativeGpu === 'function' ? window.__ortBuild.nativeGpu() : null;
    var cpuOnly = function (reason) {
        return allowWasm ? { ok: true, providers: ['wasm'], gpuReason: reason, build: null } : { ok: false, reason: reason };
    };
    if (!native) return Promise.resolve(cpuOnly('webkit'));
    if (!navigator.gpu) return Promise.resolve(cpuOnly('no_webgpu'));
    return needVerifier().then(function () {
        return window.OrtEpVerify.adapterIdentity();
    }).then(function (id) {
        adapterInfo = id || null;
        if (!id || !id.present) return cpuOnly('no_webgpu');
        if (id.fallback) return cpuOnly('software_gpu');
        var ua = (navigator.userAgent.match(/(?:Chrome|Firefox|Edg)\/(\d+)/) || [])[1] || '0';
        cacheKey = window.OrtEpVerify.verdictKey(['rmvpe', 'ort-native', id.vendor, id.architecture, 'b' + ua]);
        if (window.OrtEpVerify.isRejected(cacheKey, 'webgpu')) return cpuOnly('gpu_rejected');
        return { ok: true, providers: allowWasm ? ['webgpu', 'wasm'] : ['webgpu'], build: native, adapter: id };
    }, function () { return cpuOnly('no_webgpu'); });
}

var worker = null, ready = null, readyInfo = null, pending = {}, seq = 0, currentSlug = null;

// ── How long the network will WORK on a song — known before it starts on it ───────────────────
// The start times one chunk of its 4-second known-answer run (`chunkMs`); a song is cut into
// chunks of CORE_S seconds, each bigger than that one. Field, /audio-to-notes, 25.09–06.10.2026,
// 551 automatic passes on a graphics card: work ÷ (chunks × chunkMs) — median 2.1 (p25 1.8,
// p75 2.3, p90 2.6). With 2.1, «the work will take longer than 120 s» was wrong 6 times in 551
// (3 either way); 1.8 and 2.6 were wrong 11 and 12 times. And the chunk time is the card's own:
// two starts of one device in one session differ by a median 1.1× (p90 1.6×, 63 sessions), while
// cards differ by a hundred times — chunk under 0.4 s: the whole pass a median 21 s; 1.5–3 s:
// 128 s; over 3 s: 4–6 minutes.
var WORK_K = 2.1, CORE_S = 10.24;
function estimateWorkS(durationS, chunkMs) {
    if (!(durationS > 0) || !(chunkMs > 0)) return null;
    return WORK_K * Math.ceil(durationS / CORE_S) * chunkMs / 1000;
}
// The FASTEST chunk this card has timed lately, not the last one: a start measured while the page
// is busy reads slow (two starts of one card in one session: 1.6× apart at p90, up to 3.9×), and a
// page that skips the start on such a memory would never measure again. A remembered time predicts the next
// pass as well as a fresh one (86 passes with an earlier start in the session: predicted ÷ real
// work p25 0.87, median 0.97, p75 1.1 — fresh: 0.87 / 0.95 / 1.1). Kept per card (the key names
// the adapter and the browser build) for 30 days from the measurement it holds.
var TEMPO_KEY = 'tmb_rmvpe_tempo', TEMPO_TTL_MS = 30 * 24 * 3600 * 1000;
function tempoRead() {
    try {
        var r = JSON.parse(window.localStorage.getItem(TEMPO_KEY) || 'null');
        if (!r || !(r.c > 0) || !(r.t > 0) || Date.now() - r.t > TEMPO_TTL_MS) return null;
        if (r.k && cacheKey && r.k !== cacheKey) return null;      // another card (or browser build) timed it
        return r;
    } catch (_) { return null; }
}
function tempo() { var r = tempoRead(); return r ? { chunkMs: r.c, ageMs: Date.now() - r.t } : null; }
function noteTempo(info) {
    if (!info || info.provider !== 'webgpu' || !(info.chunkMs > 0)) return;
    var prev = tempoRead();
    // a slower start leaves the memory as it is — the date stays that of the measurement it holds
    if (prev && prev.c <= info.chunkMs) return;
    try { window.localStorage.setItem(TEMPO_KEY, JSON.stringify({ c: Math.round(info.chunkMs), t: Date.now(), k: cacheKey || null })); } catch (_) {}
}

function release() {
    if (worker) { try { worker.terminate(); } catch (_) {} }
    worker = null; ready = null; readyInfo = null;
    Object.keys(pending).forEach(function (id) { try { pending[id].reject(typed('released', 'tracker released')); } catch (_) {} });
    pending = {};
}

function onVerdict(v) {
    var flat = window.OrtEpVerify ? window.OrtEpVerify.telemetryOf(v, adapterInfo) : { ep: v && v.provider, ok: v && v.ok ? 1 : 0 };
    flat.why = v && v.why || null; flat.vf = v && v.voicedFrac; flat.acc = v && v.acc; flat.chunk_ms = v && v.chunkMs || null;
    tele(currentSlug, 'rmvpe_verdict', flat);
    // Only a WRONG ANSWER is remembered (30 days); a throw or a cancel is a bad day.
    if (v && !v.ok && !v.error && !v.skipped && v.provider === 'webgpu' && cacheKey && window.OrtEpVerify) {
        try { window.OrtEpVerify.remember(cacheKey, 'webgpu', { kind: 'kat', why: v.why || null }); } catch (_) {}
    }
}

// Deadlines of one rung: create + two known-answer runs. A hang here is a
// dead promise under a spinning button, not a slow machine (measured 23.09:
// the WASM rung after a failed WebGPU rung in the SAME ORT instance never
// answered — 8 min of silence). Visible time only: a hidden tab is not a fault.
var RUNG_DEADLINE_MS = { webgpu: 90000, wasm: 300000 };
function visibleTimeout(fn, ms) {
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    var t = setTimeout(fn, ms); return function () { clearTimeout(t); };
}

/**
 * One worker = one provider = one ORT instance. A provider that fails by
 * THROWING (not by a wrong answer) can leave the wasm module of its ORT build
 * poisoned: 2026-09-23, Chromium whose WebGPU session create threw a bare
 * number (a wasm exception pointer), the WASM rung in the same instance then
 * hung forever. So the ladder lives HERE, across workers: a failed rung costs a
 * terminate() and a fresh worker on the plain WASM build.
 */
function startRung(prov, build, bytes, transfer, slug) {
    return new Promise(function (resolve, reject) {
        var w, done = false, cancelDeadline = null;
        var settle = function (err, info) {
            if (done) return; done = true;
            if (cancelDeadline) cancelDeadline();
            if (err) { try { w && w.terminate(); } catch (_) {} if (worker === w) worker = null; reject(err); }
            else resolve(info);
        };
        try { w = new Worker(meta('rmvpe-worker-url')); }
        catch (e) { settle(typed('worker_failed', String(e && e.message || e))); return; }
        worker = w;
        w.onerror = function (ev) {
            var err = typed('worker_failed', (ev && ev.message) || 'worker error');
            if (!done) { settle(err); return; }
            Object.keys(pending).forEach(function (id) { pending[id].reject(err); });
            pending = {};
        };
        w.onmessage = function (ev) {
            var m = ev.data || {};
            if (m.type === 'verdict') onVerdict(m.verdict);
            else if (m.type === 'ready') settle(null, { provider: m.provider, chunkMs: m.chunkMs, unverified: !!m.unverified });
            else if (m.type === 'progress') { var p = pending[m.id]; if (p && p.onProgress) p.onProgress(m.done, m.of); }
            else if (m.type === 'result') { var r = pending[m.id]; if (r) { delete pending[m.id]; r.resolve(m); } }
            else if (m.type === 'error') {
                var err = typed(m.code || 'rmvpe_error', m.message);
                err.verdicts = m.verdicts || null;
                if (m.id != null && pending[m.id]) { var q = pending[m.id]; delete pending[m.id]; q.reject(err); }
                else settle(err);
            }
        };
        cancelDeadline = visibleTimeout(function () {
            tele(slug, 'rmvpe_rung_timeout', { ep: prov, ms: RUNG_DEADLINE_MS[prov] });
            settle(typed('rung_timeout', prov + ' did not become ready'));
        }, RUNG_DEADLINE_MS[prov] || 120000);
        var buf = bytes.buffer.byteLength === bytes.length ? bytes.buffer : bytes.slice().buffer;
        w.postMessage({
            type: 'init', modelBytes: buf, providers: [prov],
            ortUrl: abs(build.url), wasmBase: abs(build.wasmBase),
            ortFallbackUrl: abs(build.fallbackUrl), fallbackWasmBase: abs(build.fallbackWasmBase)
        }, transfer ? [buf] : []);
    });
}

function preload(slug, fnOrOpts) {
    var opts = typeof fnOrOpts === 'function' ? { onProgress: fnOrOpts } : (fnOrOpts || {});
    currentSlug = slug || currentSlug;
    if (ready) return ready;
    var t0 = performance.now();
    ready = support({ allowWasm: !!opts.allowWasm }).then(function (sup) {
        if (!sup.ok) throw typed('rmvpe_unsupported_' + sup.reason, 'RMVPE cannot run here: ' + sup.reason);
        var plain = { url: window.__ortBuild.url(), wasmBase: window.__ortBuild.wasmBase(),
                      fallbackUrl: window.__ortBuild.fallback(), fallbackWasmBase: window.__ortBuild.wasmBase() };
        return needDownloader().then(function () {
            var urls = [meta('rmvpe-model-url'), meta('rmvpe-model-url-fallback')].filter(Boolean);
            var tryUrl = function (i) {
                return self.__mdFetchBytes(urls[i], {
                    signal: opts.signal,
                    onProgress: function (got, total) { if (opts.onProgress && total > 0) opts.onProgress(Math.min(1, got / total), 'model'); }
                }).catch(function (e) {
                    if (i + 1 < urls.length && !(opts.signal && opts.signal.aborted)) { tele(slug, 'rmvpe_model_fallback', { reason: String(e && e.message || e).slice(0, 80) }); return tryUrl(i + 1); }
                    throw e;
                });
            };
            return tryUrl(0);
        }).then(function (bytes) {
            if (opts.onProgress) opts.onProgress(1, 'compile');
            var rungs = sup.providers.slice();
            var step = function (i) {
                var prov = rungs[i], last = i === rungs.length - 1;
                // The GPU rung runs the native build; the WASM rung ALWAYS the plain one.
                var build = prov === 'webgpu' && sup.build ? sup.build : plain;
                // Bytes are copied to every rung but the last: a failed rung must not
                // take the model with it (91 MB — a copy costs less than a re-fetch).
                return startRung(prov, build, bytes, last, slug).catch(function (e) {
                    if (last || (opts.signal && opts.signal.aborted)) throw e;
                    tele(slug, 'rmvpe_rung_fallback', { from: prov, to: rungs[i + 1], code: e && e.code || null,
                                                       msg: String(e && e.message || e).slice(0, 60) });
                    return step(i + 1);
                });
            };
            return step(0);
        });
    }).then(function (info) {
        readyInfo = info;
        noteTempo(info);
        tele(slug, 'rmvpe_preload_ok', { ms: Math.round(performance.now() - t0), ep: info.provider, chunk_ms: info.chunkMs || 0 });
        return info;
    }).catch(function (e) {
        tele(slug, 'rmvpe_preload_fail', { code: e && e.code || null, msg: String(e && e.message || e).slice(0, 80) });
        release();
        throw e;
    });
    return ready;
}

/** Mono 16 kHz — the network's passport. OfflineAudioContext downmixes (L+R)/2 and resamples. */
function to16kMono(audioBuffer) {
    var Ctor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var n = Math.max(1, Math.round(audioBuffer.duration * SR));
    var ctx = new Ctor(1, n, SR);
    var node = ctx.createBufferSource();
    node.buffer = audioBuffer; node.connect(ctx.destination); node.start();
    return ctx.startRendering().then(function (b) { return new Float32Array(b.getChannelData(0)); });
}

function abortable(promise, signal) {
    if (!signal) return promise;
    return new Promise(function (resolve, reject) {
        var stop = function () { reject(new DOMException('aborted', 'AbortError')); release(); };
        if (signal.aborted) { stop(); return; }
        signal.addEventListener('abort', stop, { once: true });
        promise.then(function (v) { signal.removeEventListener('abort', stop); resolve(v); },
                     function (e) { signal.removeEventListener('abort', stop); reject(e); });
    });
}

function vocalStats(conf) {
    var n = conf.length;
    if (!n) return { p90: 0, frac05: 0, frac07: 0 };
    var s = Array.prototype.slice.call(conf).sort(function (a, b) { return a - b; });
    var c05 = 0, c07 = 0;
    for (var i = 0; i < n; i++) { if (conf[i] >= 0.5) c05++; if (conf[i] >= 0.7) c07++; }
    return { p90: +s[Math.min(n - 1, Math.floor(0.9 * n))].toFixed(3), frac05: +(c05 / n).toFixed(3), frac07: +(c07 / n).toFixed(3) };
}

function track(audioBuffer, opts) {
    opts = opts || {};
    var slug = opts.slug, onProgress = opts.onProgress || null, signal = opts.signal || null;
    var t0 = performance.now();
    return abortable(preload(slug, {
        signal: signal, allowWasm: !!opts.allowWasm,
        onProgress: function (p, phase) { if (onProgress) onProgress(p * 0.4, phase || 'model'); }
    }).then(function (info) {
        if (typeof opts.gate === 'function' && opts.gate(info) === false) throw typed('declined', 'the caller declined after the start');
        return to16kMono(audioBuffer);
    }).then(function (pcm) {
        if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
        var id = ++seq;
        // Silence watchdog: a chunk that never comes back is a fault, not a slow
        // device — the ceiling scales with the chunk time the rung itself measured.
        var chunkMs = (readyInfo && readyInfo.chunkMs) || 2000;
        var quietMs = Math.max(120000, 40 * chunkMs);
        return new Promise(function (resolve, reject) {
            var cancelWd = null;
            var arm = function () {
                if (cancelWd) cancelWd();
                cancelWd = visibleTimeout(function () {
                    if (!pending[id]) return;
                    delete pending[id];
                    tele(slug, 'rmvpe_track_stalled', { quiet_ms: quietMs, ep: readyInfo && readyInfo.provider });
                    release();
                    reject(typed('track_stalled', 'no chunk for ' + quietMs + ' ms'));
                }, quietMs);
            };
            var end = function (fn) { return function (v) { if (cancelWd) cancelWd(); fn(v); }; };
            pending[id] = {
                resolve: end(resolve), reject: end(reject),
                onProgress: function (done, of) { arm(); if (onProgress) onProgress(0.4 + 0.6 * done / of, 'track', { done: done, of: of }); }
            };
            arm();
            worker.postMessage({ type: 'track', id: id, pcm: pcm }, [pcm.buffer]);
        });
    }), signal).then(function (m) {
        var stats = vocalStats(m.conf);
        tele(slug, 'rmvpe_track_ok', { sec: Math.round(audioBuffer.duration), ms: Math.round(performance.now() - t0), work_ms: m.ms, ep: m.provider, p90: stats.p90, f07: stats.frac07 });
        return { f0: m.f0, conf: m.conf, hop: HOP_S, frames: m.frames, provider: m.provider, ms: m.ms, stats: stats };
    }).catch(function (e) {
        if ((signal && signal.aborted) || (e && (e.name === 'AbortError' || e.code === 'cancelled' || e.code === 'released'))) throw new DOMException('aborted', 'AbortError');
        if (e && e.code === 'declined') throw e;
        tele(slug, 'rmvpe_track_fail', { code: e && e.code || null, msg: String(e && e.message || e).slice(0, 80) });
        if (e && e.code === 'gpu_output_corrupt') release();
        throw e;
    });
}

function toFrames(r) {
    var out = new Array(r.frames);
    for (var i = 0; i < r.frames; i++) out[i] = { timeS: i * HOP_S, freqHz: r.f0[i], confidence: r.conf[i] };
    return out;
}

window.RmvpePitch = {
    support: support,
    preload: preload,
    track: track,
    toFrames: toFrames,
    release: release,
    estimateWorkS: estimateWorkS,
    tempo: tempo,
    isWarm: function () { return !!readyInfo; },
    info: function () { return readyInfo; },
    // tests: what a finished start leaves in the device's memory
    _noteTempo: noteTempo
};
})();
